import { useState } from 'react'
import { View, Text, Pressable, Modal, ScrollView, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'

// Selettore tappa della pagina Pick, stesso linguaggio del RaceSwitcher (pillola con nome + stato a parole).
// Le tappe possono essere molte (fino a 21): una pillola centrale con la tappa scelta, frecce per
// andare alla precedente/successiva e, toccando la pillola, lo sheet con tutte le tappe.
// stages: [{ value, number, date, status: 'pick' | 'live' | 'done', profile: 'flat'|'hilly'|'mountain'|'itt'|'ttt'|null, data }] in ordine di tappa.
const PROFILE_ICON = { flat: 'road-variant', hilly: 'chart-line-variant', mountain: 'image-filter-hdr', itt: 'timer-outline', ttt: 'timer-sand' }
export default function StageSwitcher({ stages, selected, onSelect, hideStatus = false }) {
  const { t, i18n } = useTranslation()
  const tc = useThemeColors()
  const insets = useSafeAreaInsets()
  const [open, setOpen] = useState(false)

  const idx = stages.findIndex(st => st.value === selected)
  const cur = stages[idx] ?? stages[0]
  if (!cur) return null

  const tagOf = (status) => status === 'live' ? t('pick.race_tag_live') : status === 'done' ? t('pick.race_tag_done') : t('pick.race_tag_pick')
  const fmt = (d) => d ? new Date(d).toLocaleDateString(i18n.language, { day: '2-digit', month: 'short' }) : ''
  const go = (i) => { if (stages[i]) onSelect(stages[i]) }
  const profLabel = (p) => (p ? t(`pick.profile_${p}`) : '')

  const Arrow = ({ dir, disabled }) => (
    <Pressable
      onPress={() => go(idx + dir)}
      disabled={disabled}
      hitSlop={4}
      accessibilityRole="button"
      accessibilityLabel={t(dir < 0 ? 'pick.prev_stage' : 'pick.next_stage')}
      style={({ pressed }) => [s.arrow, { backgroundColor: tc.bgCard2, borderColor: tc.border, opacity: disabled ? 0.35 : pressed ? 0.7 : 1 }]}
    >
      <MaterialCommunityIcons name={dir < 0 ? 'chevron-left' : 'chevron-right'} size={22} color={tc.text} />
    </Pressable>
  )

  return (
    <>
      <View style={s.row}>
        <Arrow dir={-1} disabled={idx <= 0} />
        <Pressable
          onPress={() => setOpen(true)}
          accessibilityRole="button"
          accessibilityLabel={hideStatus ? `${t('common.stage')} ${cur.number}` : `${t('common.stage')} ${cur.number}, ${tagOf(cur.status)}`}
          style={({ pressed }) => [s.pill, { backgroundColor: tc.rosa, borderColor: tc.rosa }, pressed && { opacity: 0.85 }]}
        >
          {cur.profile && <MaterialCommunityIcons name={PROFILE_ICON[cur.profile]} size={22} color={tc.brandText} style={{ marginRight: 8 }} />}
          <View style={{ flex: 1 }}>
            <Text style={[s.name, { color: tc.brandText }]} numberOfLines={1}>{t('common.stage')} {cur.number}<Text style={s.of}> / {stages.length}</Text></Text>
            <View style={s.tagRow}>
              {cur.status === 'live' && <View style={[s.dot, { backgroundColor: tc.brandText }]} />}
              <Text style={[s.tag, { color: tc.brandText }]} numberOfLines={1}>{[hideStatus ? null : tagOf(cur.status), cur.date ? fmt(cur.date) : null, profLabel(cur.profile) || null].filter(Boolean).join(' · ')}</Text>
            </View>
          </View>
          <MaterialCommunityIcons name="chevron-down" size={20} color={tc.brandText} />
        </Pressable>
        <Arrow dir={1} disabled={idx >= stages.length - 1} />
      </View>

      <Modal visible={open} transparent animationType="slide" onRequestClose={() => setOpen(false)}>
        <View style={s.overlay}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setOpen(false)} accessibilityRole="button" accessibilityLabel={t('pick.close')} />
          <View style={[s.sheet, { backgroundColor: tc.bgCard, paddingBottom: insets.bottom + 12 }]}>
            <View style={[s.sheetHeader, { borderBottomColor: tc.border }]}>
              <Text style={[s.sheetTitle, { color: tc.text }]}>{t('pick.stage_list_title')}</Text>
              <Pressable onPress={() => setOpen(false)} hitSlop={6} style={[s.closeBtn, { backgroundColor: tc.bgCard2 }]} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
                <MaterialCommunityIcons name="close" size={18} color={tc.text} />
              </Pressable>
            </View>
            <ScrollView>
              {stages.map(st => {
                const on = st.value === selected
                return (
                  <Pressable
                    key={st.value}
                    onPress={() => { onSelect(st); setOpen(false) }}
                    accessibilityRole="button"
                    accessibilityState={{ selected: on }}
                    style={({ pressed }) => [s.sheetRow, { borderBottomColor: tc.border }, on && { backgroundColor: tc.rosaSubtle }, pressed && { opacity: 0.7 }]}
                  >
                    <View style={[s.num, { backgroundColor: on ? tc.rosa : tc.bgCard2 }]}>
                      <Text style={[s.numText, { color: on ? tc.brandText : tc.textMuted }]}>{st.number}</Text>
                    </View>
                    {st.profile && <MaterialCommunityIcons name={PROFILE_ICON[st.profile]} size={18} color={tc.textMuted} />}
                    <View style={{ flex: 1 }}>
                      <Text style={[s.sheetName, { color: tc.text, fontWeight: on ? '700' : '500' }]}>{t('common.stage')} {st.number}</Text>
                      <Text style={[s.sheetMeta, { color: tc.textMuted }]}>{[fmt(st.date), hideStatus ? null : tagOf(st.status), profLabel(st.profile) || null].filter(Boolean).join(' · ')}</Text>
                    </View>
                    {on && <MaterialCommunityIcons name="check" size={18} color={tc.rosaText} />}
                  </Pressable>
                )
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </>
  )
}

const s = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 12, paddingTop: 4, paddingBottom: 6 },
  arrow: { width: 44, height: 48, borderRadius: 12, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  pill: { flex: 1, minHeight: 48, flexDirection: 'row', alignItems: 'center', borderRadius: 12, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 6 },
  name: { fontFamily: fonts.condensedBold, fontSize: 16 },
  of: { fontSize: 12, fontWeight: '600', opacity: 0.8 },
  tagRow: { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 1 },
  dot: { width: 6, height: 6, borderRadius: 3 },
  tag: { fontSize: 9, fontWeight: '700', letterSpacing: 1.2 },
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '75%' },
  sheetHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 0.5 },
  sheetTitle: { fontFamily: fonts.condensedBold, fontSize: 18 },
  closeBtn: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  sheetRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 10, minHeight: 56, borderBottomWidth: 0.5 },
  num: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
  numText: { fontSize: 13, fontWeight: '800' },
  sheetName: { fontSize: 14 },
  sheetMeta: { fontSize: 11, marginTop: 2 },
})
